// The puzzle banner's words and actions, decided from the solver's state:
// the banner stands in for the game bar across the top of the review while
// a puzzle is on the board, so the puzzle is unmistakable. Pure, so it is
// tested without React; the container in `index.tsx` passes these to
// `PuzzleBanner` and the game bar yields to it through `puzzles.current()`.
import { describePrize, type Puzzle } from './finder.ts';
import { seriesWith } from './series.ts';
import type { Solving } from './solving.ts';

/** Where a puzzle stands among the game's puzzles, in ply order. */
export interface PuzzlePlace {
  /** 1-based. */
  index: number;
  count: number;
  /** The 0-based ply of the puzzle after this one in the series, if any. */
  next?: number;
}

/**
 * The puzzle's place in the game's series (series.ts: the reader's blunders
 * and misses, the same rows the stage panels list), the puzzle on the board
 * counted among them. The opening's theory puzzle stands alone, with the
 * first of the series next.
 */
export function placeOf(series: readonly number[], current: Puzzle): PuzzlePlace {
  if (current.kind === 'theory') return { index: 1, count: 1, next: series[0] };
  const all = seriesWith(series, current.ply);
  const at = all.indexOf(current.ply);
  return { index: at + 1, count: all.length, next: all[at + 1] };
}

/** "EdBlackKnight vs mmarcin55 · Oct 9", from the game's stored record. */
export function gameLineOf(record: { white: { name: string }; black: { name: string }; playedAt: string }): string {
  const date = new Date(record.playedAt);
  const when = Number.isNaN(date.getTime()) ? undefined : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${record.white.name} vs ${record.black.name}${when ? ` · ${when}` : ''}`;
}

/** What the best move achieves, as one line after solving. */
export function explanationOf(puzzle: Puzzle): string {
  const prize = puzzle.prize;
  switch (prize.kind) {
    case 'mate':
      return `Mate in ${prize.inMoves}`;
    case 'material': {
      // "A knight", "The exchange", "Material worth 2": each carries its own article.
      const text = describePrize(prize);
      return `Wins ${text.charAt(0).toLowerCase()}${text.slice(1)}`;
    }
    case 'advantage':
      return `Keeps a winning advantage (+${(prize.centipawns / 100).toFixed(1)})`;
    case 'avoid':
      return prize.delta >= 5000 ? 'Avoids a mate' : `Avoids losing ${(prize.delta / 100).toFixed(1)} pawns`;
    case 'theory':
      return describePrize(prize);
  }
}

/** The banner's props drawn from the solver: everything but its callbacks. */
export interface BannerText {
  /** The puzzle is over: solved, or its solution shown. */
  done: boolean;
  /** "Puzzle 2 of 5 · Find the better move", "Solved · Nxe5". */
  title: string;
  /** "White to move · try 1", or after solving what the move achieves. */
  line: string;
  /** Show solution is offered. */
  canShow: boolean;
  /** Try again is offered (after the solution was shown). */
  canRetry: boolean;
  /** The 0-based ply of the puzzle to go on to, if any. */
  next?: number;
}

const SIDE = { white: 'White', black: 'Black' } as const;

/**
 * The banner for a solver state. `series` is the game's series of puzzles
 * (series.ts); `bestSan` the first move of the answer in SAN, shown once the
 * puzzle is over; `motif` the puzzle's motif in words ("Pin, against the
 * queen", motif.ts), after the try count while the reader is solving, so
 * they know what they are practising.
 */
export function bannerOf(solving: Solving, series: readonly number[], bestSan: string | undefined, motif?: string): BannerText {
  const p = solving.puzzle;
  const place = placeOf(series, p);
  const theory = p.kind === 'theory';
  const task = theory ? 'Complete the opening' : p.kind === 'tactic' ? describePrize(p.prize) : 'Find the better move';
  const where = theory ? 'Opening puzzle' : `Puzzle ${place.index} of ${place.count}`;
  const answer = bestSan ? ` · ${bestSan}` : '';
  const practising = motif ? ` · ${motif}` : '';
  switch (solving.status) {
    case 'solved': {
      // The line playing out after the finding move: the moves so far, then the explanation.
      const rest = solving.playOut?.playing ? (solving.solution ?? []).slice(solving.playOut.from).join(' ') : undefined;
      const line = rest === undefined ? explanationOf(p) : `Playing it out: ${rest ? `${rest} ` : ''}…`;
      return { done: true, title: `Solved${answer}`, line, canShow: false, canRetry: false, next: place.next };
    }
    case 'shown':
      return { done: true, title: `The solution${answer}`, line: explanationOf(p), canShow: false, canRetry: true, next: place.next };
    case 'showing':
      return {
        done: false,
        title: `${where} · ${task}`,
        line: solving.solution?.length ? `Solution: ${solving.solution.join(' ')} …` : 'Playing the solution…',
        canShow: false,
        canRetry: false,
      };
    case 'thinking':
      return { done: false, title: `${where} · ${task}`, line: 'The engine is answering…', canShow: false, canRetry: false };
    case 'failed':
      return { done: false, title: `${where} · ${task}`, line: `Not that move · taking it back · try ${solving.tries}${practising}`, canShow: true, canRetry: false };
    case 'playing': {
      const step = theory ? `move ${Math.min((solving.step ?? 0) + 1, solving.needed)} of ${solving.needed} · ` : '';
      return { done: false, title: `${where} · ${task}`, line: `${SIDE[p.side]} to move · ${step}try ${solving.tries}${practising}`, canShow: true, canRetry: false };
    }
  }
}
